import { useMemo } from "react";
import { QrCode } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const YEAR = new Date().getFullYear();

export function RequestSourceReport({ quotes = [] }) {
  const rows = useMemo(() => {
    const base = MONTHS.map((m) => ({ m, link: 0, qr: 0, unknown: 0, total: 0, converted: 0, linkConv: 0, qrConv: 0 }));
    quotes.forEach((q) => {
      const d = new Date(q.createdAt || 0);
      if (q.source !== "online_link" || d.getFullYear() !== YEAR) return;
      const r = base[d.getMonth()];
      r[q.channel === "qr" ? "qr" : q.channel === "link" ? "link" : "unknown"] += 1;
      r.total += 1;
      if (q.convertedPolicyId || q.status === "Dönüştürüldü") {
        r.converted += 1;
        if (q.channel === "qr") r.qrConv += 1;
        if (q.channel === "link") r.linkConv += 1;
      }
    });
    return base;
  }, [quotes]);
  const sum = rows.reduce((a, r) => ({ link: a.link + r.link, qr: a.qr + r.qr, unknown: a.unknown + r.unknown, total: a.total + r.total, converted: a.converted + r.converted }), { link: 0, qr: 0, unknown: 0, total: 0, converted: 0 });
  const rate = (r) => (r.total ? `%${Math.round((r.converted / r.total) * 100)}` : "—");
  const shown = rows.filter((r) => r.total);
  const pct = (c, t) => (t ? Math.round((c / t) * 100) : null);
  const chart = rows.slice(0, new Date().getMonth() + 1).map((r) => ({ ay: r.m.slice(0, 3), Link: pct(r.linkConv, r.link), QR: pct(r.qrConv, r.qr) }));
  return (
    <section className="panel req-source" data-testid="request-source-report">
      <div className="panel-head">
        <div><h2><QrCode size={15} /> Online talep kaynakları · {YEAR}</h2><p>Link ve QR'dan gelen talepler ve poliçeye dönüşenler. Eski talepler "Bilinmiyor" sayılır.</p></div>
      </div>
      <div className="req-source-sum">
        <div><span>Link</span><strong data-testid="req-source-link">{sum.link}</strong></div>
        <div><span>QR</span><strong data-testid="req-source-qr">{sum.qr}</strong></div>
        <div><span>Bilinmiyor</span><strong data-testid="req-source-unknown">{sum.unknown}</strong></div>
        <div className="ok"><span>Poliçeye dönüşen</span><strong data-testid="req-source-converted">{sum.converted} <small>{rate(sum)}</small></strong></div>
      </div>
      {(sum.link > 0 || sum.qr > 0) && (
        <div className="req-source-chart" data-testid="req-source-chart">
          <h3>Kanal bazlı dönüşüm oranı (%)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chart} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="ay" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(v) => (v == null ? "Talep yok" : `%${v}`)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Link" stroke="#1a5fe0" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="QR" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {shown.length ? (
        <div className="table-scroll">
          <table className="req-source-table">
            <thead><tr><th>Ay</th><th>Link</th><th>QR</th><th>Bilinmiyor</th><th>Toplam</th><th>Poliçe</th><th>Dönüşüm</th></tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.m} data-testid={`req-source-row-${r.m}`}><td><strong>{r.m}</strong></td><td>{r.link}</td><td>{r.qr}</td><td>{r.unknown}</td><td>{r.total}</td><td>{r.converted}</td><td>{rate(r)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="muted small" data-testid="req-source-empty">Bu yıl henüz online talep yok.</p>}
    </section>
  );
}
